// AntScore web — чистая view-логика табло (без DOM).
//
// Здесь только детерминированные деривации поверх Snapshot движка:
//   - webPhase — строка фазы матча для нижней строки WEB1/WEB4;
//   - webColumns — колонки таблицы по сетам (С1 С2 С3) с текущим сетом и пустышками;
//   - resultModel — данные экрана результата WebResult (WEB5);
//   - parseMatchCode / extractShareIdFromInput — разбор кода/ссылки на экране входа (WEB2);
//   - shareUrl — построение ссылки «Следить».
//
// Всё чистое и юнит-тестируемое — DOM-рендер живёт в scoreboard/overlay/entry.

import type { Snapshot, Team } from './engine';

export interface WebColumn {
  label: string;        // С1 / С2 / С3
  a: string;            // геймы A в этом сете (или '·' для пустого)
  b: string;
  current: boolean;     // текущий (незавершённый) сет
  empty: boolean;       // ещё не сыгранный сет
}

/** Фаза матча для нижней строки табло. Канон design v6 (webPhase). */
export function webPhase(s: Snapshot): string {
  const setNo = s.completedSets.length + 1;
  if (s.status === 'finished') {
    const name = s.winner ? teamName(s, s.winner) : '';
    return `Матч окончен · победа: ${name}`;
  }
  if (s.inTieBreak) return 'Тай-брейк';
  if (s.isDeuce) return `Ровно · сет ${setNo}`;
  return `Сет ${setNo} · гейм идёт`;
}

/** Колонки таблицы по сетам: завершённые + текущий + пустышки до 3. */
export function webColumns(s: Snapshot): [WebColumn, WebColumn, WebColumn] {
  const cols: WebColumn[] = [];
  s.completedSets.forEach((set, i) => {
    cols.push({ label: `С${i + 1}`, a: String(set.a), b: String(set.b), current: false, empty: false });
  });
  if (s.status !== 'finished') {
    cols.push({
      label: `С${s.completedSets.length + 1}`,
      a: String(s.gamesA),
      b: String(s.gamesB),
      current: true,
      empty: false,
    });
  }
  while (cols.length < 3) {
    cols.push({ label: `С${cols.length + 1}`, a: '·', b: '·', current: false, empty: true });
  }
  return cols.slice(0, 3) as [WebColumn, WebColumn, WebColumn];
}

export interface ResultRow {
  name: string;
  team: Team;
  cells: { v: string; won: boolean }[]; // won — выиграл ли этот сет (для подсветки)
}

export interface ResultModel {
  status: string;           // «МАТЧ ОКОНЧЕН» / «ТЕКУЩИЙ СЧЁТ»
  badge: string;            // «ПОБЕДА» / «ВЕДЁТ»
  finished: boolean;
  winnerName: string;
  winnerTeam: Team;
  loserTeam: Team;
  bigScore: string;         // сеты победителя
  smallScore: string;       // сеты проигравшего
  cols: { label: string }[];
  winRow: ResultRow;
  loseRow: ResultRow;
  durationGames: string;    // «6 геймов» (подпись)
}

/** Модель экрана результата (WEB5 / WebResult). Работает и для «текущего» снимка. */
export function resultModel(s: Snapshot, durationLabel?: string): ResultModel {
  const finished = s.status === 'finished';
  // Набор сетов: завершённые, либо текущий как единственный (для предпросмотра).
  const sets = (s.completedSets.length
    ? s.completedSets.map((x) => ({ a: x.a, b: x.b }))
    : [{ a: s.gamesA, b: s.gamesB }]
  ).slice(0, 3);

  let winner: Team;
  if (finished && s.winner) winner = s.winner;
  else if (s.setsWonA === s.setsWonB) winner = s.gamesA >= s.gamesB ? 'A' : 'B';
  else winner = s.setsWonA > s.setsWonB ? 'A' : 'B';
  const loser: Team = winner === 'A' ? 'B' : 'A';

  const totalGames = sets.reduce((n, x) => n + x.a + x.b, 0);

  const mkRow = (team: Team): ResultRow => ({
    name: teamName(s, team),
    team,
    cells: sets.map((set) => {
      const mine = team === 'A' ? set.a : set.b;
      const opp = team === 'A' ? set.b : set.a;
      return { v: String(mine), won: mine >= opp };
    }),
  });

  const dur = durationLabel ? `${durationLabel} · ${totalGames} геймов` : `${totalGames} геймов`;

  return {
    status: finished ? 'МАТЧ ОКОНЧЕН' : 'ТЕКУЩИЙ СЧЁТ',
    badge: finished ? 'ПОБЕДА' : 'ВЕДЁТ',
    finished,
    winnerName: teamName(s, winner),
    winnerTeam: winner,
    loserTeam: loser,
    bigScore: String(winner === 'A' ? s.setsWonA : s.setsWonB),
    smallScore: String(winner === 'A' ? s.setsWonB : s.setsWonA),
    cols: sets.map((_, i) => ({ label: `С${i + 1}` })),
    winRow: mkRow(winner),
    loseRow: mkRow(loser),
    durationGames: dur,
  };
}

export function teamName(s: Snapshot, t: Team): string {
  return s.teams[t]?.name ?? (t === 'A' ? 'Синие' : 'Красные');
}

// ---------- WEB2 · разбор кода / ссылки ----------

// 6-символьный код матча: латиница (без неоднозначных) + цифры, напр. A7K32Q
// (см. MATCH_CODE_LENGTH в antscore-backend/src/api/match/services/share-code.ts).
// Допускаем дефисы/пробелы во вводе (формат A7K-32Q из шаринга) — нормализуем.
const CODE_RE = /^[A-Z0-9]{6}$/;

/** Нормализовать сырой ввод кода: верхний регистр, выкинуть всё кроме [A-Z0-9]. */
export function normalizeCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Валиден ли нормализованный 6-символьный код. */
export function isValidCode(raw: string): boolean {
  return CODE_RE.test(normalizeCode(raw));
}

/**
 * Извлечь shareId из произвольного ввода: либо код (A7K32Q / A7K-32Q),
 * либо ссылка (antscore.dev.pl.webant.ru/?share=A7K32Q, https://…/?share=…, /shared/<id>, #<id>).
 * Возвращает нормализованный shareId или null, если распознать не удалось.
 */
export function extractShareIdFromInput(raw: string): string | null {
  const text = raw.trim();
  if (!text) return null;

  // Похоже на ссылку/URL — пытаемся вытащить share-параметр или путь.
  if (/[/?#=]/.test(text) || /^https?:/i.test(text) || /\.[a-z]{2,}/i.test(text)) {
    const id = shareIdFromUrl(text);
    if (id) return id;
    // не нашли в ссылке — падаем в проверку кода ниже
  }

  // Иначе трактуем как код матча.
  const code = normalizeCode(text);
  if (CODE_RE.test(code)) return code;
  return null;
}

function shareIdFromUrl(text: string): string | null {
  // ?share=… / ?shareId=…
  const q = text.match(/[?&](?:share|shareId)=([^&#\s]+)/i);
  if (q) {
    const id = decodeURIComponent(q[1]);
    if (/^[A-Za-z0-9_-]+$/.test(id)) return id;
  }
  // /shared/<id>
  const p = text.match(/\/shared\/([A-Za-z0-9_-]+)/);
  if (p) return p[1];
  // #<id>
  const h = text.match(/#([A-Za-z0-9_-]+)\s*$/);
  if (h) return h[1];
  return null;
}

/** Построить ссылку «Следить» для отображения (хост из текущего origin, если есть). */
export function shareUrl(shareId: string, origin?: string): string {
  const host = (origin ?? '').replace(/\/+$/, '');
  if (host) return `${host}/?share=${shareId}`;
  return `antscore.dev.pl.webant.ru/?share=${shareId}`;
}
